import { DataProvider, GetManyResponse, BaseRecord } from "@refinedev/core";
import { checkTokenValidity } from "../utils/auth-debug";
// import axiosInstance from "../api/axios-config";
import { dataProvider as originalDataProvider } from "./nestjsx-crud/provider";
import { axiosInstance as nestjsxCrudAxiosInstance } from "./nestjsx-crud/utils/axios";

/**
 * Ce fournisseur de données enveloppe le fournisseur original et bloque toutes
 * les requêtes si l'utilisateur n'est pas authentifié
 */
export const createSafeDataProvider = (apiUrl: string): DataProvider => {
  const originalProvider = originalDataProvider(apiUrl, nestjsxCrudAxiosInstance);

  /**
   * Vérifie si l'utilisateur est authentifié avant de faire une requête
   * Si non authentifié, retourne une réponse vide sans erreur
   */
  const checkAuthBeforeRequest = async (
    requestFn: Function | undefined,
    ...args: any[]
  ): Promise<any> => {
    // Si la fonction n'existe pas dans le provider original, retourner une réponse vide
    if (!requestFn) {
      return { data: [] };
    }

    const token = localStorage.getItem("accessToken");

    if (!token) {
      // Retourner une réponse vide compatible avec le type attendu
      return { data: [], total: 0 };
    }

    const { isValid } = checkTokenValidity(token);
    if (!isValid) {
      localStorage.removeItem("accessToken");
      // Redirection retardée pour éviter les problèmes de rendu
      setTimeout(() => {
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }, 100);
      return { data: [], total: 0 };
    }

    // Si on arrive ici, on peut faire la requête
    return requestFn(...args);
  };

  // Implémentations par défaut pour les méthodes manquantes
  const defaultGetMany = <TData extends BaseRecord = BaseRecord>(
    params: any
  ): Promise<GetManyResponse<TData>> => {
    return Promise.resolve({ data: [] });
  };

  const defaultCreateMany = (params: any): Promise<any> => {
    return Promise.resolve({ data: [] });
  };

  const defaultUpdateMany = (params: any): Promise<any> => {
    return Promise.resolve({ data: [] });
  };

  const defaultDeleteMany = (params: any): Promise<any> => {
    return Promise.resolve({ data: [] });
  };

  return {
    getApiUrl: originalProvider.getApiUrl,

    getList: async (params) =>
      checkAuthBeforeRequest(originalProvider.getList, params),

    getOne: async (params) =>
      checkAuthBeforeRequest(originalProvider.getOne, params),

    getMany: async (params) =>
      checkAuthBeforeRequest(
        originalProvider.getMany || defaultGetMany,
        params
      ),

    create: async (params) =>
      checkAuthBeforeRequest(originalProvider.create, params),

    createMany: async (params) =>
      checkAuthBeforeRequest(
        originalProvider.createMany || defaultCreateMany,
        params
      ),

    update: async (params) =>
      checkAuthBeforeRequest(originalProvider.update, params),

    updateMany: async (params) =>
      checkAuthBeforeRequest(
        originalProvider.updateMany || defaultUpdateMany,
        params
      ),

    deleteOne: async (params) =>
      checkAuthBeforeRequest(originalProvider.deleteOne, params),

    deleteMany: async (params) =>
      checkAuthBeforeRequest(
        originalProvider.deleteMany || defaultDeleteMany,
        params
      ),

    custom: async (params) =>
      checkAuthBeforeRequest(originalProvider.custom, params),
  };
};
